import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Typography } from "antd";
import { STEP_INPUT_POLICIES } from "components/CC/config/stepInputPolicies";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { setFormSchema } from "components/CC/Nodes/SetNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { ParameterCategory } from "components/CC/UI/ParameterPicker/ParameterPicker";
import { CustomFieldData } from "components/CC/UI/ParameterPicker/parameterUtils";
import { useBuildParameterPickerCategories } from "components/CC/UI/ParameterPicker/useBuildParameterPickerCategories";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

export type SetFormData = z.infer<typeof setFormSchema>;

interface FormProps {
    onChange?: (data: SetFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
    /** Form fields from API */
    formFields?: App.Data.FormFieldData[];
    /** Custom fields from API (Integration or FormStructure) */
    customFields?: CustomFieldData[];
}

export const SetForm = ({ onChange, onRegisterValidator, formId, formFields, customFields }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<SetFormData>
            key={selectedNode?.id}
            id={formId}
            schema={setFormSchema}
            defaultValues={selectedNode?.formData as SetFormData}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                formFields={formFields}
                customFields={customFields}
            />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: SetFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

const FormContent = ({ onChange, onRegisterValidator, formFields, customFields }: FormContentProps) => {
    const { control, trigger } = useGeneralForm<SetFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { selectedNode } = useSelectedNode();
    const { t } = usePaldockTranslation("components.connection-creator");
    const buildCategories = useBuildParameterPickerCategories({
        t,
        formFields,
        customFields,
        currentNodeId: selectedNode?.id,
    });

    const categories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.set.value.ui.allowedCategories],
            }),
        [buildCategories]
    );

    const keyCategories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.set.key.ui.allowedCategories],
            }),
        [buildCategories]
    );

    const { fields, append, remove } = useFieldArray({
        control,
        name: "fields",
    });

    const handleAddField = () => {
        append({ key: "", value: "" });
    };

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<SetFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.set.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.set.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem className="flex-1 !mb-0" control={control} name="title" label={t("nodes.set.forms.title")}>
                    <Input placeholder={t("nodes.set.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.set.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.set.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Key/Value fields */}
            {fields.length > 0 && (
                <div className="mb-2">
                    {/* Header row */}
                    <div className="flex gap-2 mb-2">
                        <Typography.Text type="secondary" className="flex-1">
                            {t("nodes.set.forms.key")}
                        </Typography.Text>
                        <Typography.Text type="secondary" className="flex-1">
                            {t("nodes.set.forms.value")}
                        </Typography.Text>
                        <div className="w-8" /> {/* Space for delete button */}
                    </div>

                    {/* Field rows */}
                    {fields.map((field, index) => (
                        <SetFieldRow
                            key={field.id}
                            index={index}
                            remove={remove}
                            categories={categories}
                            keyCategories={keyCategories}
                        />
                    ))}
                </div>
            )}

            {/* Add field button */}
            <Button type="link" icon={<PlusOutlined />} onClick={handleAddField} className="!px-0">
                {t("nodes.set.forms.add_field")}
            </Button>
        </div>
    );
};

interface SetFieldRowProps {
    index: number;
    remove: (index: number) => void;
    categories: ParameterCategory[];
    keyCategories: ParameterCategory[];
}

const isKeyTagInProgress = (value?: string): boolean => Boolean(value?.startsWith("{") && !value.includes("}"));

const SetFieldRow = ({ index, remove, categories, keyCategories }: SetFieldRowProps) => {
    const { control } = useGeneralForm<SetFormData>();
    const { t } = usePaldockTranslation("components.connection-creator");
    const [isKeyEditing, setIsKeyEditing] = useState(false);

    return (
        <div className="flex gap-2 mb-2 items-start">
            <Controller
                control={control}
                name={`fields.${index}.key`}
                render={({ field, fieldState }) => {
                    const showError = Boolean(fieldState.error) && !(isKeyEditing && isKeyTagInProgress(field.value));

                    return (
                        <Form.Item
                            className="flex-1 !mb-0"
                            validateStatus={showError ? "error" : undefined}
                            help={showError ? fieldState.error?.message : undefined}
                        >
                            <div
                                onFocus={() => setIsKeyEditing(true)}
                                onBlur={(event) => {
                                    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                                    setIsKeyEditing(false);
                                    field.onBlur();
                                }}
                            >
                                <TagTextInput
                                    value={field.value ?? ""}
                                    onChange={field.onChange}
                                    placeholder={t("nodes.set.forms.key_placeholder")}
                                    categories={keyCategories}
                                    mode={STEP_INPUT_POLICIES.set.key.ui.mode}
                                    maxTags={1}
                                />
                            </div>
                        </Form.Item>
                    );
                }}
            />
            <FormItem className="flex-1 !mb-0" control={control} name={`fields.${index}.value`}>
                <TagTextInput
                    placeholder={t("nodes.set.forms.value_placeholder")}
                    categories={categories}
                    mode={STEP_INPUT_POLICIES.set.value.ui.mode}
                />
            </FormItem>
            <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => remove(index)}
                className="!text-gray-400 hover:!text-red-500"
            />
        </div>
    );
};
